import { Component, AfterViewInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { Subject, takeUntil } from 'rxjs';
import { RouterOutlet } from '@angular/router';

declare global {
  interface Window {
    triggerUpdate?: (seconds?: number) => void;
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterOutlet],
})
export class AppComponent implements AfterViewInit, OnDestroy {
  public updateAvailable = false;
  public countdown = 60;
  private countdownInterval: any;
  private destroy$ = new Subject<void>();
  private updateCheckInterval: any;

  constructor(private swUpdate: SwUpdate) {
    const updated = localStorage.getItem('app-updated');
    if (updated === 'true') {
      localStorage.removeItem('app-updated');
      return;
    }

    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.pipe(takeUntil(this.destroy$)).subscribe(event => {
        if (event.type === 'VERSION_READY') {
          this.showUpdateAlert();
        }
      });
    }
  }

  public ngOnInit() {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.checkForUpdate();

      this.updateCheckInterval = setInterval(() => {
        this.swUpdate.checkForUpdate();
      }, 60 * 1000);
    }
  }

  public ngOnDestroy() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    if (this.updateCheckInterval) {
      clearInterval(this.updateCheckInterval);
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  public ngAfterViewInit(): void {
    if (!environment.production) {
      window.triggerUpdate = (seconds: number = 60) => {
        console.warn(`[DEV] Simulating update with ${seconds}s countdown...`);
        this.showUpdateAlert(seconds);
      };
    }
  }

  public showUpdateAlert(seconds: number = 60) {
    this.updateAvailable = true;
    this.countdown = seconds;

    this.countdownInterval = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        clearInterval(this.countdownInterval);
        this.reloadApp();
      }
    }, 1000);
  }

  public reloadApp() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    localStorage.setItem('app-updated', 'true');

    this.swUpdate.activateUpdate().then(() => document.location.reload());
  }
}
